import { parseISO } from "date-fns";
import { useMemo, useState } from "react";

import type { AssetBookingDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { User } from "@/components/User/User";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { timeWithoutSeconds } from "@/helpers/date";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useTranslation } from "@/translations";

import { DeclineBookingModal } from "./DeclineBookingModal";

type BookingListModalProps = ModalBaseProps & {
  bookings: AssetBookingDto[];
};

export function BookingListModal({ isOpened, onOpenChange, bookings }: BookingListModalProps): React.ReactNode {
  const [bookingToBeDeclined, setBookingToBeDeclined] = useState<AssetBookingDto | undefined>();

  const { t } = useTranslation();
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<AssetBookingDto>();

    return [
      helper.accessor("startTime", {
        header: t("page.bookings.asset-detail.booking-list-modal.timeslot"),
        cell: ({ row }) => (
          <span className="">
            {timeWithoutSeconds(row.original.startTime)}-{timeWithoutSeconds(row.original.endTime)}
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.DATE_TIME.MIN,
        size: TABLE_COLUMN_SIZES.DATE_TIME.DEFAULT,
      }),
      helper.accessor("author", {
        header: t("page.bookings.asset-detail.booking-list-modal.author"),
        cell: ({ row }) => row.original.author && <User size="sm" user={row.original.author} />,
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
      }),
      helper.accessor("bookingReason", {
        header: t("page.bookings.asset-detail.booking-list-modal.reason"),
        cell: ({ row }) => <span className="block py-space-xs">{row.original.bookingReason}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: ({ row }) =>
          row.original.canCancel && (
            <IconButton
              data-testid="decline-booking-btn"
              variant="filled"
              tone="error"
              onClick={() => {
                setBookingToBeDeclined(row.original);
              }}
              title={t("page.bookings.asset-detail.booking-list-modal.cancel")}
              icon={<Trash01Icon />}
            />
          ),
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t]);

  const tableInstance = useResizableTable(TABLE_KEYS.BOOKINGS, {
    columns,
    state: {
      columnVisibility: {
        bookingReason: bookings[0]?.asset?.requireBookingReason,
      },
    },
    data: bookings,
    enableSorting: false,
  });

  return (
    <>
      <Modal.Root
        title={t("page.bookings.asset-detail.booking-list-modal.title")}
        description={
          bookings.length > 1 && !!bookings[0].date && <FormattedDate date={parseISO(bookings[0].date)} format="date" />
        }
        size="fullscreen"
        {...{ isOpened, onOpenChange }}
      >
        <Modal.Body>
          <Table table={tableInstance} />
        </Modal.Body>
      </Modal.Root>
      {bookingToBeDeclined && (
        <DeclineBookingModal
          booking={bookingToBeDeclined}
          isOpened={!!bookingToBeDeclined}
          onOpenChange={(state) => {
            if (!state) {
              setBookingToBeDeclined(undefined);
              onOpenChange(false);
            }
          }}
        />
      )}
    </>
  );
}
